/**
 * API Service - Atlas viewer
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
export const TERRAIN_BASE = import.meta.env.VITE_TERRAIN_URL || '/terrain';
const TITILER_BASE = import.meta.env.VITE_TITILER_URL || '/titiler';
const MARTIN_BASE = import.meta.env.VITE_MARTIN_URL || '/martin';

// Build absolute tile URL — handles both relative (/martin) and absolute (https://...) bases
function martinTileUrl(sourceLayer: string): string {
  if (MARTIN_BASE.startsWith('http')) return `${MARTIN_BASE}/${sourceLayer}/{z}/{x}/{y}`;
  return `${window.location.origin}${MARTIN_BASE}/${sourceLayer}/{z}/{x}/{y}`;
}

// OSM Context Layers — Portugal PMTiles extract (Protomaps basemap schema)
const OSM_TILES_BASE = import.meta.env.VITE_OSM_TILES_URL || '/osm-tiles';
export const OSM_PMTILES_URL = `pmtiles://${OSM_TILES_BASE}/portugal.pmtiles`;

import { getAccessToken } from '@/lib/auth';

/**
 * Fetch order from Analyzer API (authenticated)
 * Returns polygon, bounds, and layer tile info
 */
export async function fetchOrder(orderId: string): Promise<{
  polygon: GeoJSON.Polygon;
  bounds: [number, number, number, number];
  mdtTileUrl: string | null;
  mdtDgtTileUrl: string | null;
  hillshadeTileUrl: string | null;
  hillshadeDgtTileUrl: string | null;
  hillshadeHypsoTileUrl: string | null;
  hillshadeHypsoDgtTileUrl: string | null;
  contoursTileUrl: string | null;
  contoursSourceLayer: string | null;
  contoursItemId: string | null;
  contoursDgtTileUrl: string | null;
  contoursDgtSourceLayer: string | null;
  contoursDgtItemId: string | null;
  slopeCogPath: string | null;
  aspectCogPath: string | null;
  geomorphonsCogPath: string | null;
  erosionRiskCogPath: string | null;
  windExpositionCogPath: string | null;
  windHillshadeCogPath: string | null;
  streamsTileUrl: string | null;
  streamsSourceLayer: string | null;
  streamsItemId: string | null;
  ridgesTileUrl: string | null;
  ridgesSourceLayer: string | null;
  ridgesItemId: string | null;
  flowAccCogPath: string | null;
  basinsTileUrl: string | null;
  basinsSourceLayer: string | null;
  basinsItemId: string | null;
  keypointsTileUrl: string | null;
  keypointsSourceLayer: string | null;
  keypointsItemId: string | null;
  primaryValleysTileUrl: string | null;
  primaryValleysSourceLayer: string | null;
  primaryValleysItemId: string | null;
  primaryRidgesTileUrl: string | null;
  primaryRidgesSourceLayer: string | null;
  primaryRidgesItemId: string | null;
  floodZonesTileUrl: string | null;
  floodZonesSourceLayer: string | null;
  floodZonesItemId: string | null;
} | null> {
  try {
    // Try authenticated first, fall back to public endpoint
    const token = await getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let response = await fetch(`${API_BASE}/orders/${orderId}`, { headers });
    if (response.status === 401) {
      // No session on this domain — use public endpoint
      response = await fetch(`${API_BASE}/orders/${orderId}/public`);
    }
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Erro ao carregar pedido: ${response.statusText}`);
    }

    const order = await response.json();
    const items = order.items || [];
    if (items.length === 0) return null;

    // Parse polygon from first item
    let polygon: GeoJSON.Polygon;
    const raw = items[0].polygon_geojson;
    polygon = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (!polygon?.coordinates?.[0]) return null;

    const bounds = getBoundsFromPolygon(polygon);
    if (!bounds) return null;

    // Find MDT item (mdt-2m or mdt-50cm)
    const mdtItem = items.find(
      (i: any) => (i.product_code === 'mdt-2m' || i.product_code === 'mdt-50cm' ||
                   i.product_type === 'mdt-2m' || i.product_type === 'mdt-50cm') &&
                  i.status === 'ready' && i.tile_url
    );
    const mdtTileUrl = mdtItem?.tile_url || null;

    // Find MDT DGT item (from LOLA 50cm LiDAR)
    const mdtDgtItem = items.find(
      (i: any) => (i.product_code === 'mdt-dgt' || i.product_type === 'mdt-dgt') &&
                  i.status === 'ready' && i.tile_url
    );
    const mdtDgtTileUrl = mdtDgtItem?.tile_url || null;

    // Find hillshade item
    const hillshadeItem = items.find(
      (i: any) => (i.product_code === 'hillshade' || i.product_type === 'hillshade') &&
                  i.status === 'ready' && i.tile_url
    );
    const hillshadeTileUrl = hillshadeItem?.tile_url || null;

    // Find hillshade DGT item (from LOLA 50cm LiDAR)
    const hillshadeDgtItem = items.find(
      (i: any) => (i.product_code === 'hillshade-dgt') && i.status === 'ready' && i.tile_url
    );
    const hillshadeDgtTileUrl = hillshadeDgtItem?.tile_url || null;

    // Find hillshade-hypsometric item (3-band RGB, no colormap needed)
    const hillshadeHypsoItem = items.find(
      (i: any) => (i.product_code === 'hillshade-hypsometric' || i.product_type === 'hillshade-hypsometric') &&
                  i.status === 'ready' && i.tile_url
    );
    const hillshadeHypsoTileUrl = hillshadeHypsoItem?.tile_url || null;

    // Find hillshade-hypsometric DGT item (from LOLA 50cm LiDAR)
    const hillshadeHypsoDgtItem = items.find(
      (i: any) => (i.product_code === 'hillshade-hypsometric-dgt') && i.status === 'ready' && i.tile_url
    );
    const hillshadeHypsoDgtTileUrl = hillshadeHypsoDgtItem?.tile_url || null;

    // Find contours item (vector tiles via Martin)
    const contoursItem = items.find(
      (i: any) => (i.product_code === 'contours-5m' || i.product_code === 'contours-1m' ||
                   i.product_type === 'contours-5m' || i.product_type === 'contours-1m') &&
                  i.status === 'ready' && i.tile_url
    );
    // Martin tile URLs are relative — make absolute for MapLibre web workers
    let contoursTileUrl: string | null = null;
    let contoursSourceLayer: string | null = null;
    const contoursItemId: string | null = contoursItem?.id || null;
    if (contoursItem?.tile_url) {
      const url = contoursItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        // Extract source-layer name from URL pattern: /martin/{table}/{z}/{x}/{y}
        const parts = url.replace('/martin/', '').split('/');
        contoursSourceLayer = parts[0]; // e.g. 'order_contours'
        contoursTileUrl = martinTileUrl(contoursSourceLayer);
      } else {
        contoursTileUrl = url;
      }
    }

    // Find DGT contours item (vector tiles via Martin — from LOLA 50cm/2m DTM)
    const contoursDgtItem = items.find(
      (i: any) => (i.product_code === 'contours-dgt' || i.product_type === 'contours-dgt') &&
                  i.status === 'ready' && i.tile_url
    );
    let contoursDgtTileUrl: string | null = null;
    let contoursDgtSourceLayer: string | null = null;
    const contoursDgtItemId: string | null = contoursDgtItem?.id || null;
    if (contoursDgtItem?.tile_url) {
      const url = contoursDgtItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        contoursDgtSourceLayer = parts[0];
        contoursDgtTileUrl = martinTileUrl(contoursDgtSourceLayer);
      } else {
        contoursDgtTileUrl = url;
      }
    }

    // Find slope item (raster COG)
    const slopeItem = items.find(
      (i: any) => i.product_code === 'slope' && i.status === 'ready' && i.tile_url
    );
    const slopeCogPath: string | null = slopeItem?.tile_url || null;

    // Find aspect item (raster COG)
    const aspectItem = items.find(
      (i: any) => i.product_code === 'aspect' && i.status === 'ready' && i.tile_url
    );
    const aspectCogPath: string | null = aspectItem?.tile_url || null;

    // Find geomorphons item (raster COG)
    const geomorphonsItem = items.find(
      (i: any) => i.product_code === 'geomorphons' && i.status === 'ready' && i.tile_url
    );
    const geomorphonsCogPath: string | null = geomorphonsItem?.tile_url || null;

    // Find erosion-risk item (raster COG)
    const erosionRiskItem = items.find(
      (i: any) => i.product_code === 'erosion-risk' && i.status === 'ready' && i.tile_url
    );
    const erosionRiskCogPath: string | null = erosionRiskItem?.tile_url || null;

    // Find wind-exposition item (raster COG)
    const windExpositionItem = items.find(
      (i: any) => i.product_code === 'wind-exposition' && i.status === 'ready' && i.tile_url
    );
    const windExpositionCogPath: string | null = windExpositionItem?.tile_url || null;

    // Build companion hillshade gamma1 COG path (same order/item dir)
    let windHillshadeCogPath: string | null = null;
    if (windExpositionCogPath) {
      windHillshadeCogPath = windExpositionCogPath.replace('wind_exposition.tif', 'hillshade_gamma1.tif');
    }

    // Find streams item (vector tiles via Martin)
    const streamsItem = items.find(
      (i: any) => i.product_code === 'streams' && i.status === 'ready' && i.tile_url
    );
    let streamsTileUrl: string | null = null;
    let streamsSourceLayer: string | null = null;
    const streamsItemId: string | null = streamsItem?.id || null;
    if (streamsItem?.tile_url) {
      const url = streamsItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        streamsSourceLayer = parts[0];
        streamsTileUrl = martinTileUrl(streamsSourceLayer);
      } else {
        streamsTileUrl = url;
      }
    }

    // Find ridges item (vector tiles via Martin)
    const ridgesItem = items.find(
      (i: any) => i.product_code === 'ridges' && i.status === 'ready' && i.tile_url
    );
    let ridgesTileUrl: string | null = null;
    let ridgesSourceLayer: string | null = null;
    const ridgesItemId: string | null = ridgesItem?.id || null;
    if (ridgesItem?.tile_url) {
      const url = ridgesItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        ridgesSourceLayer = parts[0];
        ridgesTileUrl = martinTileUrl(ridgesSourceLayer);
      } else {
        ridgesTileUrl = url;
      }
    }

    // Find flow-accumulation item (raster COG)
    const flowAccItem = items.find(
      (i: any) => i.product_code === 'flow-accumulation' && i.status === 'ready' && i.tile_url
    );
    const flowAccCogPath: string | null = flowAccItem?.tile_url || null;

    // Find basins item (vector tiles via Martin)
    const basinsItem = items.find(
      (i: any) => i.product_code === 'basins' && i.status === 'ready' && i.tile_url
    );
    let basinsTileUrl: string | null = null;
    let basinsSourceLayer: string | null = null;
    const basinsItemId: string | null = basinsItem?.id || null;
    if (basinsItem?.tile_url) {
      const url = basinsItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        basinsSourceLayer = parts[0];
        basinsTileUrl = martinTileUrl(basinsSourceLayer);
      } else {
        basinsTileUrl = url;
      }
    }

    // Find keypoints item (vector tiles via Martin)
    const keypointsItem = items.find(
      (i: any) => i.product_code === 'keypoints' && i.status === 'ready' && i.tile_url
    );
    let keypointsTileUrl: string | null = null;
    let keypointsSourceLayer: string | null = null;
    const keypointsItemId: string | null = keypointsItem?.id || null;
    if (keypointsItem?.tile_url) {
      const url = keypointsItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        keypointsSourceLayer = parts[0];
        keypointsTileUrl = martinTileUrl(keypointsSourceLayer);
      } else {
        keypointsTileUrl = url;
      }
    }

    // Find primary valleys item (vector tiles via Martin)
    const primaryValleysItem = items.find(
      (i: any) => i.product_code === 'primary-valleys' && i.status === 'ready' && i.tile_url
    );
    let primaryValleysTileUrl: string | null = null;
    let primaryValleysSourceLayer: string | null = null;
    const primaryValleysItemId: string | null = primaryValleysItem?.id || null;
    if (primaryValleysItem?.tile_url) {
      const url = primaryValleysItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        primaryValleysSourceLayer = parts[0];
        primaryValleysTileUrl = martinTileUrl(primaryValleysSourceLayer);
      } else {
        primaryValleysTileUrl = url;
      }
    }

    // Find primary ridges item (vector tiles via Martin)
    const primaryRidgesItem = items.find(
      (i: any) => i.product_code === 'primary-ridges' && i.status === 'ready' && i.tile_url
    );
    let primaryRidgesTileUrl: string | null = null;
    let primaryRidgesSourceLayer: string | null = null;
    const primaryRidgesItemId: string | null = primaryRidgesItem?.id || null;
    if (primaryRidgesItem?.tile_url) {
      const url = primaryRidgesItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        primaryRidgesSourceLayer = parts[0];
        primaryRidgesTileUrl = martinTileUrl(primaryRidgesSourceLayer);
      } else {
        primaryRidgesTileUrl = url;
      }
    }

    // Find flood zones item (vector tiles via Martin)
    const floodZonesItem = items.find(
      (i: any) => i.product_code === 'flood-zones' && i.status === 'ready' && i.tile_url
    );
    let floodZonesTileUrl: string | null = null;
    let floodZonesSourceLayer: string | null = null;
    const floodZonesItemId: string | null = floodZonesItem?.id || null;
    if (floodZonesItem?.tile_url) {
      const url = floodZonesItem.tile_url as string;
      if (url.startsWith('/martin/')) {
        const parts = url.replace('/martin/', '').split('/');
        floodZonesSourceLayer = parts[0];
        floodZonesTileUrl = martinTileUrl(floodZonesSourceLayer);
      } else {
        floodZonesTileUrl = url;
      }
    }

    return { polygon, bounds, mdtTileUrl, mdtDgtTileUrl, hillshadeTileUrl, hillshadeDgtTileUrl, hillshadeHypsoTileUrl, hillshadeHypsoDgtTileUrl, contoursTileUrl, contoursSourceLayer, contoursItemId, contoursDgtTileUrl, contoursDgtSourceLayer, contoursDgtItemId, slopeCogPath, aspectCogPath, geomorphonsCogPath, erosionRiskCogPath, windExpositionCogPath, windHillshadeCogPath, streamsTileUrl, streamsSourceLayer, streamsItemId, ridgesTileUrl, ridgesSourceLayer, ridgesItemId, flowAccCogPath, basinsTileUrl, basinsSourceLayer, basinsItemId, keypointsTileUrl, keypointsSourceLayer, keypointsItemId, primaryValleysTileUrl, primaryValleysSourceLayer, primaryValleysItemId, primaryRidgesTileUrl, primaryRidgesSourceLayer, primaryRidgesItemId, floodZonesTileUrl, floodZonesSourceLayer, floodZonesItemId };
  } catch (error) {
    console.error('Error fetching order:', error);
    return null;
  }
}

/**
 * Fetch COG statistics from TiTiler to get dynamic min/max
 */
export async function fetchCogStatistics(cogPath: string): Promise<{ min: number; max: number } | null> {
  try {
    const encodedPath = encodeURIComponent(cogPath);
    const resp = await fetch(`${TITILER_BASE}/cog/statistics?url=${encodedPath}`, {
      signal: AbortSignal.timeout(10000),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    // TiTiler returns { "b1": { "min": ..., "max": ..., ... } }
    const band = data['b1'] || data[Object.keys(data)[0]];
    if (!band) return null;
    return { min: band.min, max: band.max };
  } catch {
    return null;
  }
}

/**
 * Fetch COG statistics clipped to a polygon (POST with GeoJSON feature)
 * Returns min/max only within the polygon area
 */
export async function fetchCogStatisticsClipped(
  cogPath: string,
  polygon: GeoJSON.Polygon
): Promise<{ min: number; max: number } | null> {
  try {
    const encodedPath = encodeURIComponent(cogPath);
    const feature = {
      type: 'Feature',
      geometry: polygon,
      properties: {},
    };
    const resp = await fetch(`${TITILER_BASE}/cog/statistics?url=${encodedPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feature),
      signal: AbortSignal.timeout(15000),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    // POST returns GeoJSON Feature: { properties: { statistics: { b1: { min, max, ... } } } }
    const stats = data?.properties?.statistics;
    if (!stats) return null;
    const band = stats['b1'] || stats[Object.keys(stats)[0]];
    if (!band) return null;
    return { min: band.min, max: band.max };
  } catch {
    return null;
  }
}

/**
 * Fetch COG statistics clipped to a polygon for all bands (RGB)
 * Returns per-band min/max within the polygon area
 */
export async function fetchCogStatisticsClippedRGB(
  cogPath: string,
  polygon: GeoJSON.Polygon
): Promise<{ b1: { min: number; max: number }; b2: { min: number; max: number }; b3: { min: number; max: number } } | null> {
  try {
    const encodedPath = encodeURIComponent(cogPath);
    const feature = {
      type: 'Feature',
      geometry: polygon,
      properties: {},
    };
    const resp = await fetch(`${TITILER_BASE}/cog/statistics?url=${encodedPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feature),
      signal: AbortSignal.timeout(15000),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    const stats = data?.properties?.statistics;
    if (!stats) return null;
    const b1 = stats['b1'];
    const b2 = stats['b2'];
    const b3 = stats['b3'];
    if (!b1 || !b2 || !b3) return null;
    return {
      b1: { min: b1.min, max: b1.max },
      b2: { min: b2.min, max: b2.max },
      b3: { min: b3.min, max: b3.max },
    };
  } catch {
    return null;
  }
}

/**
 * Fetch TileJSON metadata from TiTiler for minzoom/maxzoom
 */
export async function fetchTileJSON(cogPath: string, rescale: string, colormap?: string): Promise<{ minzoom: number; maxzoom: number } | null> {
  try {
    const encodedPath = encodeURIComponent(cogPath);
    let url = `${TITILER_BASE}/cog/tilejson.json?url=${encodedPath}&tile_format=png&rescale=${rescale}`;
    if (colormap) url += `&colormap_name=${colormap}`;
    const resp = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!resp.ok) return null;
    const data = await resp.json();
    return { minzoom: data.minzoom ?? 0, maxzoom: data.maxzoom ?? 18 };
  } catch {
    return null;
  }
}

/**
 * Build TiTiler tile URL for MDT layer
 * Uses spectral_r colormap (blue→green→yellow→orange→red) matching QGIS style
 */
export function buildMdtTileUrl(cogPath: string, rescale: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&colormap_name=spectral_r&rescale=${rescale}`;
}

/**
 * Build TiTiler tile URL for Hillshade layer (B&W grayscale)
 * No colormap — single-band renders as grayscale natively
 */
export function buildHillshadeTileUrl(cogPath: string, rescale: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=${rescale}`;
}

/**
 * Build TiTiler tile URL for Hypsometric Hillshade layer (3-band RGB)
 * No colormap or rescale needed — RGB auto-detected by TiTiler
 */
export function buildHillshadeHypsoTileUrl(cogPath: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}`;
}

/**
 * Build TiTiler tile URL for Hypsometric Hillshade Local (3-band RGB with per-band rescale)
 */
export function buildHillshadeHypsoLocalTileUrl(
  cogPath: string,
  rescaleR: string,
  rescaleG: string,
  rescaleB: string,
): string {
  const encodedPath = encodeURIComponent(cogPath);
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=${rescaleR}&rescale=${rescaleG}&rescale=${rescaleB}`;
}

/**
 * Build TiTiler tile URL for Slope (Declives) layer
 * Uses custom discrete 7-class colormap per QML spec (values in degrees)
 * Percent→Degree thresholds: 5%=2.86°, 8%=4.57°, 15%=8.53°, 25%=14.04°, 30%=16.70°, 50%=26.57°
 */
export function buildSlopeTileUrl(cogPath: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  // TiTiler discrete colormap: JSON mapping pixel values (after rescale) to RGBA
  // Rescale 0-45 maps degrees to 0-255 range; we use colormap intervals on the raw values
  // Using interval colormap: each entry defines the color for values >= that key
  const colormap = encodeURIComponent(JSON.stringify([
    [[0, 3], [68, 119, 43, 255]],
    [[3, 5], [102, 157, 29, 255]],
    [[5, 9], [158, 213, 103, 255]],
    [[9, 14], [232, 169, 9, 255]],
    [[14, 17], [234, 83, 31, 255]],
    [[17, 27], [204, 39, 8, 255]],
    [[27, 255], [143, 17, 17, 255]],
  ]));
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=0,45&colormap=${colormap}`;
}

/**
 * Build TiTiler tile URL for Geomorphons (10 Landforms) layer
 * 10 discrete landform classes (integer values 1-10) per Geomorphons.qml
 */
export function buildGeomorphonsTileUrl(cogPath: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  const colormap = encodeURIComponent(JSON.stringify({
    "1": [113, 113, 113, 255],
    "2": [83, 5, 14, 255],
    "3": [186, 34, 49, 255],
    "4": [212, 95, 32, 255],
    "5": [229, 204, 91, 255],
    "6": [233, 233, 152, 255],
    "7": [166, 186, 98, 255],
    "8": [17, 90, 21, 255],
    "9": [105, 129, 149, 255],
    "10": [0, 0, 0, 255],
  }));
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&colormap=${colormap}`;
}

/**
 * Build TiTiler tile URL for Aspect (Exposição Solar) layer
 * 10 discrete compass direction classes (0-360°) per aspect.qml
 */
export function buildAspectTileUrl(cogPath: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  const colormap = encodeURIComponent(JSON.stringify([
    [[0, 23], [65, 66, 165, 255]],
    [[23, 68], [65, 149, 255, 255]],
    [[68, 113], [246, 239, 14, 255]],
    [[113, 158], [245, 182, 19, 255]],
    [[158, 203], [210, 36, 10, 255]],
    [[203, 248], [180, 131, 180, 255]],
    [[248, 293], [128, 38, 161, 255]],
    [[293, 338], [32, 150, 190, 255]],
    [[338, 361], [65, 66, 165, 255]],
  ]));
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=0,360&colormap=${colormap}`;
}

/**
 * Build TiTiler tile URL for Flow Accumulation layer
 * Log10-transformed + gamma-corrected raster. QML teal-to-navy gradient.
 * Critical rule: ALWAYS logarithmic. Values 0-6 (log10 scale after preprocessing).
 */
export function buildFlowAccTileUrl(cogPath: string, rescale: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=${rescale}&colormap_name=blues`;
}

/**
 * Build TiTiler tile URL for Erosion Risk (STI/LS factor) layer
 * Regrarians YlOrRd diverging: green (low) → yellow → red (high)
 * Doc spec: #1A9641→#A6D96A→#FFFFBF→#FDAE61→#D7191C (NÃO Sequential Reds)
 */
export function buildErosionRiskTileUrl(cogPath: string, rescale: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  const colormap = encodeURIComponent(JSON.stringify([
    [[0, 2], [26, 150, 65, 255]],
    [[2, 5], [166, 217, 106, 255]],
    [[5, 15], [255, 255, 191, 255]],
    [[15, 25], [253, 174, 97, 255]],
    [[25, 255], [215, 25, 28, 255]],
  ]));
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=${rescale}&colormap=${colormap}`;
}

/**
 * Build TiTiler tile URL for Wind Exposition layer
 * 5 discrete classes per Wind Exposition.qml:
 * Sheltered (#2b83ba) → Partially sheltered (#abdda4) → Neutral (#ffffbf)
 * → Partially exposed (#fdae61) → Very Exposed (#d7191c)
 * Values centered on 1.0 (neutral), range ~0.8-1.2
 */
export function buildWindExpositionTileUrl(cogPath: string): string {
  const encodedPath = encodeURIComponent(cogPath);
  const colormap = encodeURIComponent(JSON.stringify([
    [[0, 88], [43, 131, 186, 255]],
    [[88, 95], [171, 221, 164, 255]],
    [[95, 103], [255, 255, 191, 255]],
    [[103, 110], [253, 174, 97, 255]],
    [[110, 255], [215, 25, 28, 255]],
  ]));
  return `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodedPath}&rescale=0.7,1.3&colormap=${colormap}`;
}

/**
 * Calculate bounds [west, south, east, north] from a GeoJSON polygon
 */
export function getBoundsFromPolygon(polygon: GeoJSON.Polygon): [number, number, number, number] | null {
  try {
    if (!polygon?.coordinates?.[0]) return null;

    const coords = polygon.coordinates[0];
    let minLng = Infinity, minLat = Infinity;
    let maxLng = -Infinity, maxLat = -Infinity;

    for (const coord of coords) {
      const [lng, lat] = coord;
      if (typeof lng === 'number' && typeof lat === 'number') {
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
      }
    }

    if (minLng === Infinity) return null;
    return [minLng, minLat, maxLng, maxLat];
  } catch {
    return null;
  }
}
